/** @format */

import { createSlice } from "@reduxjs/toolkit";
import {
	registerPaymentMethod,
	getPaymentMethods,
	getDefaultPaymentMethod,
	updatePaymentMethod,
} from "../actions/paymentMethodAction";

export interface IPaymentMethod {
	id: string;
	brand: string;
	last4: string;
	expMonth: number;
	expYear: number;
	isDefault: boolean;
	createdAt: string;
}

interface PaymentMethodState {
	paymentMethods: IPaymentMethod[];
	defaultPaymentMethod: IPaymentMethod | null;
	isLoading: boolean;
	error: string | null;
	successMessage: string | null;
}

const initialState: PaymentMethodState = {
	paymentMethods: [],
	defaultPaymentMethod: null,
	isLoading: false,
	error: null,
	successMessage: null,
};

const paymentMethodSlice = createSlice({
	name: "paymentMethod",
	initialState,
	reducers: {
		clearPaymentMethodError: (state) => {
			state.error = null;
		},
		clearPaymentMethodSuccess: (state) => {
			state.successMessage = null;
		},
	},
	extraReducers: (builder) => {
		// Register Payment Method
		builder.addCase(registerPaymentMethod.pending, (state) => {
			state.isLoading = true;
			state.error = null;
			state.successMessage = null;
		});
		builder.addCase(registerPaymentMethod.fulfilled, (state, action) => {
			state.isLoading = false;
			state.successMessage = "Card registered successfully!";
			// Add the new payment method to the list
			if (action.payload?.data) {
				state.paymentMethods.push(action.payload.data);
				// If it's the default, set it
				if (action.payload.data.isDefault) {
					state.defaultPaymentMethod = action.payload.data;
				}
			}
		});
		builder.addCase(registerPaymentMethod.rejected, (state, action) => {
			state.isLoading = false;
			state.error = action.payload as string;
		});

		builder.addCase(updatePaymentMethod.pending, (state) => {
			state.isLoading = true;
			state.error = null;
			state.successMessage = null;
		});
		builder.addCase(updatePaymentMethod.fulfilled, (state, action) => {
			state.isLoading = false;
			state.successMessage = "Card updated successfully!";
			// Add the new payment method to the list
			if (action.payload?.data) {
				state.paymentMethods.push(action.payload.data);
				// If it's the default, set it
				if (action.payload.data.isDefault) {
					state.defaultPaymentMethod = action.payload.data;
				}
			}
		});
		builder.addCase(updatePaymentMethod.rejected, (state, action) => {
			state.isLoading = false;
			state.error = action.payload as string;
		});

		// Get All Payment Methods
		builder.addCase(getPaymentMethods.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});
		builder.addCase(getPaymentMethods.fulfilled, (state, action) => {
			state.isLoading = false;
			state.paymentMethods = action.payload?.data || [];
		});
		builder.addCase(getPaymentMethods.rejected, (state, action) => {
			state.isLoading = false;
			state.error = action.payload as string;
		});

		// Get Default Payment Method
		builder.addCase(getDefaultPaymentMethod.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});
		builder.addCase(getDefaultPaymentMethod.fulfilled, (state, action) => {
			state.isLoading = false;
			state.defaultPaymentMethod = action.payload?.data || null;
		});
		builder.addCase(getDefaultPaymentMethod.rejected, (state, action) => {
			state.isLoading = false;
			state.error = action.payload as string;
		});
	},
});

export const { clearPaymentMethodError, clearPaymentMethodSuccess } =
	paymentMethodSlice.actions;

export default paymentMethodSlice.reducer;
